/*jshint esversion: 11 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label
} from 'reactstrap';
import { connect } from 'react-redux';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageDataLoader from '../Utility/PageDataLoader';
import Paginator from '../Utility/Paginator';
import { getStaffList } from '../../api/staff';
import { getLeaveBalance } from '../../api/attendance';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import _ from 'lodash';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const animatedComponents = makeAnimated();

const StaffLeaveBalancePage = ({ authUser }) => {
  const breadcrumbItems = [{ title: 'Leave Balance', link: '#' }];

  const [staffList, setStaffList] = useState([]);
  const [staffBalances, setStaffBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterCount, setFilterCount] = useState('');
  const [selectedStaffOption, setSelectedStaffOption] = useState([]);
  const [filteredBalances, setFilteredBalances] = useState([]);
  const [sortingRule, setSortingRule] = useState('name$asc');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageLimit: 10,
    totalEntries: 0,
    totalPages: 0
  });

  const [displayedBalances, setDisplayedBalances] = useState([]);

  const selectStyles = {
    menu: (base) => ({
      ...base,
      zIndex: 100
    })
  };

  function openFilterModal() {
    setFilterModalOpen(true);
  }

  function closeFilterModal() {
    setFilterModalOpen(false);
  }

  useEffect(() => {
      fetchStaffBalances();
  }, [authUser]);

  async function fetchStaffBalances() {
    setLoading(true);

    const response = await getStaffList(1, 100);
    if (!response) return;

    const staff = response.data;
    setStaffList(staff);

    const results = await Promise.all(
      staff.map(async (s) => {
        const balance = await getLeaveBalance(s._id);

        const paid = balance?.paid_consumed || 0;
        const unpaid = balance?.unpaid_consumed || 0;
        const sat = balance?.saturday_used || 0;

        return {
          id: s._id,
          name: s.name,
          role: s.role?.role_name,
          paid,
          unpaid,
          sat,
          total: paid + unpaid + sat
        };
      })
    );
    setStaffBalances(results);
    setFilteredBalances(results);
    paginateData(1, pagination.pageLimit);
    setLoading(false);
  }

  function buildStaffOptions(staffArray) {
    return staffArray.map((i) => ({
      label: `${i.name} - ${i.role?.role_name}`,
      value: i._id
    }));
  }

  const staffOptions = useMemo(() => buildStaffOptions(staffList), [staffList]);

  function applyFilter() {
    let list = [...staffBalances];

    if (selectedStaffOption.length > 0) {
      const ids = selectedStaffOption.map((i) => i.value);
      list = list.filter((i) => ids.includes(i.id));
    }

    if (filterCount) {
      const num = Number(filterCount);
      list = list.filter(
        (i) => i.paid >= num || i.unpaid >= num || i.sat >= num
      );
    }

    setFilteredBalances(list);
    paginateData(1, pagination.pageLimit);
    closeFilterModal();
  }

  function sortData(list, rule) {
    const [field, order] = rule.split('$');

    return _.orderBy(
      list,
      [(i) => (field === 'name' ? i.name : i.total)],
      [order]
    );
  }

  function paginateData(
    page = 1,
    limit = pagination.pageLimit,
    sort = sortingRule
  ) {
    let list = [...filteredBalances];

    list = sortData(list, sort);

    const totalEntries = list.length;
    const totalPages = Math.ceil(totalEntries / limit);

    const start = (page - 1) * limit;
    const end = start + limit;

    const paginated = list.slice(start, end);

    setDisplayedBalances(paginated);

    setPagination({
      currentPage: page,
      pageLimit: limit,
      totalEntries,
      totalPages
    });
  }

  useEffect(() => {
    paginateData(1, pagination.pageLimit);
  }, [filteredBalances]);

  const handlePagination = (page) => {
    paginateData(page, pagination.pageLimit);
  };

  function resetFilter() {
    setFilterCount('');
    setSelectedStaffOption([]);
    setFilteredBalances(staffBalances);
    paginateData(1, pagination.pageLimit);
    closeFilterModal();
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Modal isOpen={filterModalOpen} backdrop="static">
          <ModalHeader toggle={closeFilterModal}>Filter</ModalHeader>

          <ModalBody>
            <FormGroup>
              <Label>Select Staff</Label>
              <Select
                components={animatedComponents}
                options={staffOptions}
                value={selectedStaffOption}
                onChange={(e) => setSelectedStaffOption(e || [])}
                isMulti
                isClearable
                styles={selectStyles}
              />
            </FormGroup>

            <FormGroup>
              <Label>Paid / Unpaid / Sat ≥</Label>
              <Input
                type="number"
                value={filterCount}
                onChange={(e) => setFilterCount(e.target.value)}
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <Button size="sm" color="primary" onClick={applyFilter}>
              Apply Filter
            </Button>

            <Button size="sm" color="success" onClick={resetFilter}>
              Reset
            </Button>

            <Button size="sm" color="light" onClick={closeFilterModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        <Container fluid>
          <Breadcrumbs
            title="Staff Leave Balance"
            breadcrumbItems={breadcrumbItems}
          />

          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md="2">
                      <FormGroup>
                        <Label>Show Entries</Label>
                        <select
                          className="form-control"
                          value={pagination.pageLimit}
                          onChange={(e) => {
                            const newLimit = Number(e.target.value);
                            paginateData(1, newLimit);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </FormGroup>
                    </Col>

                    <Col md="3">
                      <FormGroup>
                        <Label>Sort</Label>
                        <select
                          className="form-control"
                          value={sortingRule}
                          onChange={(e) => {
                            setSortingRule(e.target.value);
                            paginateData(
                              1,
                              pagination.pageLimit,
                              e.target.value
                            );
                          }}
                        >
                          <option value="name$asc">Name (A-Z)</option>
                          <option value="name$desc">Name (Z-A)</option>
                          <option value="total$desc">
                            Total Leave (High-Low)
                          </option>
                          <option value="total$asc">
                            Total Leave (Low-High)
                          </option>
                        </select>
                      </FormGroup>
                    </Col>

                    <Col className="d-flex justify-content-end align-items-center">
                      <Button
                        size="sm"
                        color="primary"
                        onClick={openFilterModal}
                      >
                        <i className="ri-filter-fill mr-2"></i>
                        Filter Attendance
                      </Button>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="text-center">
                      <PageDataLoader />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table bordered striped responsive>
                        <thead>
                          <tr>
                            <th>SL No</th>
                            <th>Staff Name</th>
                            <th>Paid Leave</th>
                            <th>Unpaid Leave</th>
                            <th>Sat</th>
                            <th>Total</th>
                          </tr>
                        </thead>

                        <tbody>
                          {displayedBalances.map((item, index) => (
                            <tr key={item.id}>
                              <td>
                                {index +
                                  1 +
                                  (pagination.currentPage - 1) *
                                    pagination.pageLimit}
                              </td>
                              <td>
                                <Link to={`/staff`}>
                                  {(item.name || '-') +
                                    (item.role
                                      ? ` [${_.upperCase(item.role)}]`
                                      : '')}
                                </Link>
                              </td>
                              <td>{item.paid}</td>
                              <td>{item.unpaid}</td>
                              <td>{item.sat}</td>
                              <td>
                                <strong>{item.total}</strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  <Row className="mt-2">
                    <Col md="5">
                      <p>
                        <strong>
                          Showing{' '}
                          {(pagination.currentPage - 1) * pagination.pageLimit +
                            1}{' '}
                          to{' '}
                          {Math.min(
                            pagination.currentPage * pagination.pageLimit,
                            pagination.totalEntries
                          )}{' '}
                          of {pagination.totalEntries} entries
                        </strong>
                      </p>
                    </Col>

                    {pagination.totalPages > 0 && (
                      <Col md="7">
                        <Paginator
                          totalPages={pagination.totalPages}
                          currentPage={pagination.currentPage}
                          changePage={handlePagination}
                        />
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    authUser: state.Login?.user
  };
};

export default connect(mapStateToProps)(StaffLeaveBalancePage);
